import { useState } from "react";
import ClassNames from "Helpers/Common";
import { CLIENT_PAGE_SIZE, STATUS_STYLES } from "./constants";

/* ------------------------------- badges ---------------------------------- */

export const StatusBadge = ({ status }) => (
  <span
    className={ClassNames(
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize",
      STATUS_STYLES[status] || STATUS_STYLES.unconfigured
    )}
  >
    {status}
  </span>
);

export const Badge = ({ tone = "gray", children }) => (
  <span
    className={ClassNames(
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
      tone === "green" && "bg-green-100 text-green-800",
      tone === "yellow" && "bg-yellow-100 text-yellow-800",
      tone === "red" && "bg-red-100 text-red-800",
      tone === "gray" && "bg-gray-100 text-gray-500"
    )}
  >
    {children}
  </span>
);

/* -------------------------------- cards ---------------------------------- */

export const StatCard = ({ label, value, sub, alert }) => (
  <div className="rounded-lg bg-white flex flex-col px-6 py-4 gap-1 shadow-lg">
    <span className="text-sm font-medium text-gray-500">{label}</span>
    <span className={ClassNames("text-2xl font-semibold", alert ? "text-red-600" : "sapphire")}>
      {value}
    </span>
    {sub && <span className="text-xs text-gray-400">{sub}</span>}
  </div>
);

export const AlertBanner = ({ tone = "red", children }) => (
  <div
    className={ClassNames(
      "rounded-md px-4 py-3 text-sm",
      tone === "red" && "bg-red-50 text-red-800 border border-red-200",
      tone === "yellow" && "bg-yellow-50 text-yellow-800 border border-yellow-200"
    )}
  >
    {children}
  </div>
);

/* --------------------------- section heading ----------------------------- */

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between">
    <span className="text-lg font-medium sapphire">{children}</span>
    {action}
  </div>
);

/* -------------------------------- tables --------------------------------- */

export const PaginationFooter = ({ page, totalPages, total, onPage }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
      <span className="text-sm text-gray-400">
        {total} row{total === 1 ? "" : "s"} · page {page} of {totalPages}
      </span>
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className={ClassNames(
          "px-3 py-1 rounded-md text-sm border border-gray-300 bg-white",
          page <= 1 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-gray-50"
        )}
      >
        Previous
      </button>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        className={ClassNames(
          "px-3 py-1 rounded-md text-sm border border-gray-300 bg-white",
          page >= totalPages ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-gray-50"
        )}
      >
        Next
      </button>
    </div>
  );
};

export const SectionTable = ({ title, headers, footer, children }) => (
  <div className="flex flex-col gap-4">
    {title && <span className="text-lg font-medium sapphire">{title}</span>}
    <div className="rounded-lg bg-white shadow-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100">
        <thead>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="px-6 py-4 text-left text-xs font-normal text-gray-400 align-bottom"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">{children}</tbody>
      </table>
      {footer}
    </div>
  </div>
);

export const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-6 py-6 text-sm text-gray-400 text-center">
      {text}
    </td>
  </tr>
);

// client-side pagination for lists the backend returns whole
export const usePagedRows = (rows, perPage = CLIENT_PAGE_SIZE) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));
  const safePage = Math.min(page, totalPages);
  return {
    pageRows: rows.slice((safePage - 1) * perPage, safePage * perPage),
    footer: (
      <PaginationFooter page={safePage} totalPages={totalPages} total={rows.length} onPage={setPage} />
    ),
  };
};

/* -------------------------------- charts --------------------------------- */

const seriesTickLabel = (date, bucket) => {
  if (bucket === "hour") return `${Number(date.slice(11, 13))}h`;
  if (bucket === "day") return `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;
  return date; // month: YYYY-MM
};

export const SeriesChart = ({ title, series, bucket, tone = "bg-blue-chill" }) => {
  const max = Math.max(1, ...series.map((s) => s.count));
  const total = series.reduce((a, s) => a + s.count, 0);
  const tickEvery = Math.max(1, Math.ceil(series.length / 12));
  return (
    <div className="rounded-lg bg-white shadow-lg px-6 py-4 flex flex-col gap-3">
      <div className="flex justify-between items-baseline">
        <span className="text-sm font-medium text-gray-500">{title}</span>
        <span className="text-xs text-gray-400">{total} total</span>
      </div>
      {series.length === 0 ? (
        <span className="text-sm text-gray-400 py-6 text-center">No data in period</span>
      ) : (
        <>
          <div className="flex items-end gap-px h-24">
            {series.map((s) => (
              <div
                key={s.date}
                className={ClassNames("flex-1 rounded-t", tone, s.count === max ? "" : "opacity-40")}
                style={{ height: `${Math.max(s.count ? 3 : 0, (s.count / max) * 88)}px` }}
                title={`${s.count} · ${s.date}`}
              />
            ))}
          </div>
          <div className="flex gap-px">
            {series.map((s, i) => (
              <span key={s.date} className="flex-1 text-center text-xs text-gray-400 truncate">
                {i % tickEvery === 0 ? seriesTickLabel(s.date, bucket) : ""}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const FunnelBar = ({ label, count, base, dropNote }) => {
  const pctOfBase = base ? Math.round((count / base) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500 w-32">{label}</span>
      <div className="flex-1 bg-gray-100 rounded h-6 relative">
        <div
          className="h-6 rounded bg-blue-chill opacity-90"
          style={{ width: `${Math.min(100, pctOfBase)}%` }}
        />
        <span className="absolute inset-y-0 left-2 flex items-center text-xs font-medium text-white">
          {count}
        </span>
      </div>
      <span className="text-sm text-gray-400 w-36 text-right">
        {pctOfBase}%{dropNote ? ` · ${dropNote}` : ""}
      </span>
    </div>
  );
};

/* ------------------------------ loaders ---------------------------------- */

// Indeterminate bar under the header. Rendered whenever the active query is
// fetching (incl. range change) so there is an instant reaction instead of the
// old 2–3s of dead air.
export const TopProgressBar = ({ active }) => (
  <div className="h-0.5 w-full overflow-hidden bg-transparent">
    {active && (
      <div className="h-full w-1/3 bg-blue-chill rounded-full animate-ops-progress" />
    )}
  </div>
);

const shimmer = "animate-pulse bg-gray-100 rounded";

export const StatCardSkeleton = () => (
  <div className="rounded-lg bg-white flex flex-col px-6 py-4 gap-2 shadow-lg">
    <div className={ClassNames(shimmer, "h-3 w-24")} />
    <div className={ClassNames(shimmer, "h-7 w-16")} />
    <div className={ClassNames(shimmer, "h-3 w-32")} />
  </div>
);

export const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <div className="rounded-lg bg-white shadow-lg px-6 py-4 flex flex-col gap-3">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex gap-4">
        {Array.from({ length: cols }).map((_, c) => (
          <div key={c} className={ClassNames(shimmer, "h-4 flex-1", c === 0 && "max-w-[40%]")} />
        ))}
      </div>
    ))}
  </div>
);

// Shape-matching first-load skeleton. `shape` = { cards, tables, charts }.
export const TabSkeleton = ({ cards = 4, tables = 1, charts = 0 }) => (
  <div className="flex flex-col gap-6">
    {cards > 0 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: cards }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    )}
    {Array.from({ length: charts }).map((_, i) => (
      <TableSkeleton key={`c${i}`} rows={3} cols={6} />
    ))}
    {Array.from({ length: tables }).map((_, i) => (
      <TableSkeleton key={`t${i}`} rows={5} cols={5} />
    ))}
  </div>
);
